import type { OfflineLedgerEntry } from '@/lib/offline/offline-queue'
import { isTauri } from '@/lib/utils/tauri'

import type { CacheOwner } from './cache-scope'
import type { DisposablePersister } from './throttled-persister'
import {
  adoptLegacyOfflineSnapshot,
  CACHE_PREFIX,
  cacheOwnerPrefix,
} from './cache-scope'
import { createIndexedDBPersister } from './idb-persister'
import { createSQLitePersister } from './sqlite-persister'
import { createSerializedThrottledPersister } from './throttled-persister'

const PERSIST_THROTTLE_MS = 1500

export async function createPersister(
  scopeKey: string,
  legacyEntries: () => readonly OfflineLedgerEntry[] = () => [],
): Promise<DisposablePersister | null> {
  // Change the schema only when the persisted query shape becomes incompatible.
  // A deployment timestamp must not invalidate otherwise reusable offline data.
  const cacheKey = `${CACHE_PREFIX}${scopeKey}-writer-${crypto.randomUUID()}-schema-1`
  const scopePrefix = `${CACHE_PREFIX}${scopeKey}-`

  try {
    const [userId, login, organizationId, relationshipId] = JSON.parse(
      scopeKey,
    ) as [string, string, string, string | null]
    const legacyPrefix = `scrambl-query-cache-${JSON.stringify([userId, organizationId, relationshipId])}-`
    const create = isTauri() ? createSQLitePersister : createIndexedDBPersister
    const inner = await create(cacheKey, scopePrefix)
    const ownWriter = await create(cacheKey)
    const legacy = await create(legacyPrefix + 'schema-1', legacyPrefix)
    return createSerializedThrottledPersister(
      {
        ...inner,
        removeClient: ownWriter.removeClient,
        restoreClient: async () => {
          const current = await inner.restoreClient()
          if (current) return current
          return adoptLegacyOfflineSnapshot(
            await legacy.restoreClient(),
            { userId, login },
            organizationId,
            legacyEntries(),
          )
        },
      },
      PERSIST_THROTTLE_MS,
    )
  } catch {
    console.warn(
      '[QueryCache] Failed to create persister, running without cache',
    )
    return null
  }
}

/** Teardown removes only the captured login's organizations, never a newer login. */
export async function removeOwnerCaches(owner: CacheOwner): Promise<void> {
  const prefix = cacheOwnerPrefix(owner)
  if (isTauri()) {
    const { removeSQLiteCaches } = await import('./sqlite-persister')
    await removeSQLiteCaches(prefix)
  } else {
    const { removeIndexedDBCaches } = await import('./idb-persister')
    await removeIndexedDBCaches(prefix)
  }
}
